/**
 * 📅 [Phase 56.5 — 2026-07-09] Dialog "Déplacer le contrat (nouvelle date début)"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L1544-1660).
 *
 * Rôle : le propriétaire clique sur "Modifier dates" d'un BT dans le calendrier,
 * cette modal ouvre un shadcn Calendar avec les badges CCQ (jour férié / congé)
 * et permet de choisir une nouvelle date de début. Le BT est décalé en
 * gardant la même durée.
 *
 * Contrat :
 *   - `data` : payload showEditDates = { bon: {...} } OU null
 *   - `selectedDate` : la date sélectionnée dans le Calendar (Date object)
 *   - `onSelectDate(newDate)` : callback quand on clique une date
 *   - `onCancel()` : ferme sans persister
 *   - `onConfirm()` : appelle confirmEditDates() du parent
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { fr } from 'date-fns/locale';
import { isJourFerieCCQ, isCongeCCQ } from '@/lib/ccq-holidays';

// 🆕 Locale "semaine commence dimanche" (identique à celle du parent).
const frSunday = {
  ...fr,
  options: {
    ...fr.options,
    weekStartsOn: 0,
  },
  localize: {
    ...fr.localize,
    day: (n, opts) => {
      const days = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
      return days[n];
    }
  }
};

export default function EditDatesDialog({ data, selectedDate, onSelectDate, onCancel, onConfirm }) {
  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={() => onCancel?.()}>
      <DialogContent className="w-[450px] max-w-[95vw]">
        <DialogHeader className="text-left">
          <DialogTitle>Deplacer le contrat</DialogTitle>
          <DialogDescription>
            Selectionnez la nouvelle date de debut. Le contrat sera deplace en gardant la meme duree ({(data.bon.dates_chantier || []).length} jours).
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Chef d'equipe:</strong> {data.bon.chef_equipe_nom || 'Non assigne'}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Dates actuelles:</strong> {(() => {
                const dates = [...(data.bon.dates_chantier || [])].sort();
                if (dates.length === 0) return 'Aucune date';
                const first = new Date(dates[0] + 'T12:00:00');
                const last = new Date(dates[dates.length - 1] + 'T12:00:00');
                return `${first.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })} → ${last.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })}`;
              })()}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Duree:</strong> {(data.bon.dates_chantier || []).length} jours
            </p>
          </div>

          <div className="flex flex-col items-center">
            <p className="text-sm font-medium mb-2">Nouvelle date de debut :</p>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={onSelectDate}
              defaultMonth={(() => {
                const dates = [...(data.bon.dates_chantier || [])].sort();
                if (dates.length > 0) {
                  return new Date(dates[0] + 'T12:00:00');
                }
                return new Date();
              })()}
              locale={frSunday}
              weekStartsOn={0}
              formatters={{
                formatWeekdayName: (day) => ['D', 'L', 'M', 'M', 'J', 'V', 'S'][day.getDay()]
              }}
              modifiers={{
                jourFerie: (date) => !!isJourFerieCCQ(date.toISOString().split('T')[0]),
                congeCCQ: (date) => !!isCongeCCQ(date.toISOString().split('T')[0]),
              }}
              modifiersStyles={{
                jourFerie: { backgroundColor: 'rgb(254 202 202 / 0.7)' },
                congeCCQ: { backgroundColor: 'rgb(254 240 138 / 0.7)' },
              }}
              modifiersClassNames={{
                jourFerie: 'ccq-ferie',
                congeCCQ: 'ccq-conge',
              }}
              disabled={(date) => {
                const day = date.getDay();
                return day === 0 || day === 6;
              }}
              className="rounded-md border"
              components={{
                DayContent: ({ date }) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const jourFerie = isJourFerieCCQ(dateStr);
                  const conge = isCongeCCQ(dateStr);
                  return (
                    <div className="relative w-full h-full flex items-center justify-center">
                      {jourFerie && (
                        <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-red-500 rounded-full" title={jourFerie.nom} />
                      )}
                      {conge && !jourFerie && (
                        <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-yellow-500 rounded-full" title={conge.nom} />
                      )}
                      <span>{date.getDate()}</span>
                    </div>
                  );
                }
              }}
            />
            {selectedDate && (
              <p className="mt-2 text-sm text-blue-600 font-medium">
                Nouveau debut : {selectedDate.toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
            {/* Légende CCQ */}
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-3">
              <div className="flex items-center gap-1">
                <div className="w-4 h-3 bg-red-200 border border-red-300 rounded-sm" />
                <span>Jour ferie CCQ</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-3 bg-yellow-200 border border-yellow-300 rounded-sm" />
                <span>Conge CCQ</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-2">
          <Button variant="outline" onClick={() => onCancel?.()}>
            Annuler
          </Button>
          <Button
            onClick={() => onConfirm?.()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!selectedDate}
          >
            Confirmer la modification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
